import { computed, ref, watch } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import LeadsAPI from 'dashboard/api/leads';

/**
 * Composable que recupera o(s) lead(s) atrelado(s) a uma conversa específica
 * para uso no header da conversa (badge "Lead").
 *
 * - Só executa quando a feature flag CRM está habilitada na conta.
 * - Faz uma requisição leve à `v2/leads?conversation_id=...&per_page=1` quando
 *   a conversa muda; armazena o resultado num ref local (sem poluir a store).
 */
export function useConversationLead(conversationIdRef) {
  const accountId = useMapGetter('getCurrentAccountId');
  const isFeatureEnabledOnAccount = useMapGetter(
    'accounts/isFeatureEnabledonAccount'
  );

  const isCrmEnabled = computed(() =>
    isFeatureEnabledOnAccount.value(accountId.value, FEATURE_FLAGS.CRM)
  );

  const lead = ref(null);
  const isLoading = ref(false);

  const fetch = async () => {
    if (!isCrmEnabled.value || !conversationIdRef.value) {
      lead.value = null;
      return;
    }
    isLoading.value = true;
    try {
      const { data } = await LeadsAPI.index({
        conversation_id: conversationIdRef.value,
        per_page: 1,
      });
      lead.value = data?.leads?.[0] || null;
    } catch (e) {
      lead.value = null;
    } finally {
      isLoading.value = false;
    }
  };

  watch(
    () => [conversationIdRef.value, isCrmEnabled.value],
    () => fetch(),
    { immediate: true }
  );

  return { lead, isLoading, refresh: fetch, isCrmEnabled };
}
